import { matchPath } from 'react-router-dom'

/**
 * Route-driven app chrome (header/footer) configuration for the mobile-first
 * redesign. Each public route declares which chrome regime it gets:
 *
 * - 'default': the regular Header (mobile 56px TopBar / desktop bar) + Footer.
 *   `showBack` adds a slim back strip under the bar (all breakpoints); the
 *   bars themselves never change. `titleKey` (a key inside the
 *   `layouts:header` namespace) puts the page title next to the Back chip,
 *   and `backFallback` is where Back goes when there is no in-app history
 *   (deep-link entry).
 * - 'auth': no global Header/Footer at all — the auth pages own their chrome
 *   (full-bleed brand-blue page with its own logo row).
 *
 * Routes not listed here fall through to DEFAULT_CHROME (logo TopBar +
 * footer), which is today's behavior for every logged-in surface.
 *
 * The immersive surfaces (player, test) declare their whole chrome here —
 * wallet chip, footer, fixed header — instead of pages imperatively toggling
 * LayoutProvider flags (which flashed the default chrome for a render and
 * leaked un-reset flags onto the next route).
 */
export type ChromeKind = 'default' | 'auth'

export type ChromeMeta = {
	chrome: ChromeKind
	/** layouts:header.* key for the page title shown in the back strip */
	titleKey?: string
	/** show the slim back strip under the header bar */
	showBack?: boolean
	/** where Back goes when there is no in-app history (deep-link entry) */
	backFallback?: string
	/** hide the logged-in AppNav frame (immersive player/test surfaces) */
	navHidden?: boolean
	/** hide the header's wallet chip (immersive player/test surfaces) */
	walletHidden?: boolean
	/** false = the header scrolls with the page instead of staying fixed */
	fixedHeader?: boolean
	/** hide the global Footer — true everywhere, or 'mobile' only */
	footerHidden?: boolean | 'mobile'
}

type ChromeParams = Record<string, string | undefined>
type ChromeRule = Omit<ChromeMeta, 'backFallback'> & {
	patterns: string[]
	backFallback?: string | ((params: ChromeParams) => string)
}

/* Every drill-down page (public and logged-in) gets the back strip; the
   AppNav tab roots, landing, auth, test, and 404 don't. First match wins —
   NEVER add a '*' catch-all here, it would force the strip onto every route.
   The player and test surfaces are immersive: header stays, AppNav doesn't. */
const RULES: ChromeRule[] = [
	// '/verify-device' belongs here even though the student IS authenticated on
	// it. Without it the route falls through to DEFAULT_CHROME, and because
	// showAppNav is `isAuth && !authChrome && !chrome.navHidden && !embed`, all
	// four conjuncts hold — so the challenge renders with the full header,
	// footer and bottom nav, and one tap walks the student out of it into a
	// portal where every request 401s and bounces them back.
	{ patterns: ['/login', '/signup/*', '/forgot-password', '/reset-password', '/verify-device'], chrome: 'auth', navHidden: true },
	{
		patterns: ['/classroom/:label/course/:cId/*', '/classroom/:label/course/:cId'],
		chrome: 'default',
		navHidden: true,
		walletHidden: true,
		showBack: true,
		titleKey: 'chrome_course',
		backFallback: p => `/classroom/${p.label}`,
	},
	// The standalone-course player is the same immersive surface on its own URL.
	{
		patterns: ['/courses/:label/course/:cId/*', '/courses/:label/course/:cId'],
		chrome: 'default',
		navHidden: true,
		walletHidden: true,
		showBack: true,
		titleKey: 'chrome_course',
		backFallback: p => `/courses/${p.label}`,
	},
	// No back strip mid-test — the test surface has its own submit/exit flow.
	{ patterns: ['/test/:id'], chrome: 'default', navHidden: true, walletHidden: true, fixedHeader: false, footerHidden: 'mobile' },
	{ patterns: ['/activity/wallet'], chrome: 'default', showBack: true, titleKey: 'chrome_wallet', backFallback: '/activity' },
	{ patterns: ['/account/devices'], chrome: 'default', showBack: true, titleKey: 'chrome_settings', backFallback: '/account' },
	{ patterns: ['/results'], chrome: 'default', showBack: true, titleKey: 'chrome_explore', backFallback: '/search' },
	{ patterns: ['/instructor/:label'], chrome: 'default', showBack: true, titleKey: 'chrome_instructor', backFallback: '/search' },
	{ patterns: ['/faqs'], chrome: 'default', showBack: true, titleKey: 'chrome_faqs', backFallback: '/' },
	{
		patterns: ['/about', '/about-us', '/our-team', '/contact-us', '/terms-of-use', '/privacy-policy'],
		chrome: 'default',
		showBack: true,
		backFallback: '/',
	},
	{ patterns: ['/classroom/:label'], chrome: 'default', showBack: true, titleKey: 'chrome_classroom', backFallback: '/search' },
	{
		patterns: ['/classroom/:label/courses/:cId'],
		chrome: 'default',
		showBack: true,
		titleKey: 'chrome_course',
		backFallback: p => `/classroom/${p.label}`,
	},
	// Standalone catalog fallbacks mirror the CoursesRedirect/BundlesRedirect targets.
	{ patterns: ['/courses/:label'], chrome: 'default', showBack: true, backFallback: '/search?tab=courses' },
	{ patterns: ['/courses/:label/courses/:cId'], chrome: 'default', showBack: true, backFallback: p => `/courses/${p.label}` },
	{ patterns: ['/bundles/:label'], chrome: 'default', showBack: true, backFallback: '/search?tab=bundles' },
	{
		patterns: ['/dashboard/classrooms/:id', '/dashboard/classrooms/:id/*'],
		chrome: 'default',
		showBack: true,
		titleKey: 'chrome_classroom',
		backFallback: '/dashboard/classrooms',
	},
]

export const DEFAULT_CHROME: ChromeMeta = { chrome: 'default' }

export const resolveChrome = (pathname: string): ChromeMeta => {
	for (const rule of RULES) {
		for (const pattern of rule.patterns) {
			const match = matchPath({ path: pattern, end: true }, pathname)
			if (match) {
				const { patterns, backFallback, ...meta } = rule
				return {
					...meta,
					backFallback: typeof backFallback === 'function' ? backFallback(match.params) : backFallback,
				}
			}
		}
	}
	return DEFAULT_CHROME
}
